import type React from "react";
import { DropdownMenu as M } from "radix-ui";
import { createContext, useContext, useRef, type ReactElement, type ReactNode } from "react";

import { cx } from "@/lib/cx";

import { rememberMenuTrigger } from "./focusReturn";

const TriggerContext = createContext<React.RefObject<HTMLElement | null> | null>(null);

export function Menu({
  trigger,
  children,
  align = "end",
  side = "bottom",
  label,
}: {
  trigger: ReactElement;
  children: ReactNode;
  align?: "start" | "end" | "center";
  side?: "top" | "right" | "bottom" | "left";
  label?: string;
}) {
  const triggerRef = useRef<HTMLElement | null>(null);
  return (
    <M.Root>
      <M.Trigger asChild ref={triggerRef as React.RefObject<HTMLButtonElement>}>
        {trigger}
      </M.Trigger>
      <M.Portal>
        <M.Content
          align={align}
          side={side}
          sideOffset={4}
          aria-label={label}
          className="animate-pop-in z-50 min-w-44 rounded-md border border-line bg-surface-raised p-1 shadow-md"
        >
          <TriggerContext.Provider value={triggerRef}>{children}</TriggerContext.Provider>
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

export function MenuItem({
  children,
  onSelect,
  icon,
  destructive,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}) {
  const triggerRef = useContext(TriggerContext);
  return (
    <M.Item
      disabled={disabled}
      onSelect={() => {
        rememberMenuTrigger(triggerRef?.current ?? null);
        onSelect?.();
      }}
      className={cx(
        "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none",
        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-hover",
        destructive ? "text-danger-text" : "text-fg",
      )}
    >
      {icon && <span className="text-fg-subtle [&>svg]:size-4">{icon}</span>}
      {children}
    </M.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <M.Label className="px-2 pt-1.5 pb-1 text-xs font-medium text-fg-subtle">{children}</M.Label>;
}

export function MenuSeparator() {
  return <M.Separator className="my-1 h-px bg-line" />;
}

export function MenuRadioGroup<T extends string>({
  value,
  onValueChange,
  options,
}: {
  value: T;
  onValueChange: (value: T) => void;
  options: Array<{ value: T; label: ReactNode; description?: string }>;
}) {
  return (
    <M.RadioGroup value={value} onValueChange={(v) => onValueChange(v as T)}>
      {options.map((o) => (
        <M.RadioItem
          key={o.value}
          value={o.value}
          className="flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-hover"
        >
          <span className="mt-1.5 flex size-2 shrink-0 items-center justify-center rounded-full border border-line-strong">
            <M.ItemIndicator className="size-2 rounded-full bg-accent" />
          </span>
          <span>
            <span className="block font-medium">{o.label}</span>
            {o.description && <span className="block text-xs text-fg-subtle">{o.description}</span>}
          </span>
        </M.RadioItem>
      ))}
    </M.RadioGroup>
  );
}
